import { useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@volca/ui/components/ui/tabs";
import { EditValueChange } from "@/payroll/ai-script/ai-script-persistent-edits/shared";
import { ExampleNote } from "./examples/example-ui";
import { PracticeGrid } from "./examples/practice-grid";
import { GuidePreview } from "./guide-preview";
import { RerunReviewActions } from "./rerun-review-actions";
import {
  makeRerunWorkbook,
  RERUN_SCENARIOS,
  type RerunDecision,
  type RerunScenario,
} from "./rerun-scenarios";

export const PayrollRerunExample = () => {
  const [scenario, setScenario] = useState<RerunScenario>("changed");
  const [decisions, setDecisions] = useState<
    Partial<Record<RerunScenario, RerunDecision>>
  >({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [version, setVersion] = useState(0);
  const decision = decisions[scenario] ?? null;
  const sheet = makeRerunWorkbook(scenario, decision);
  const needsReview =
    !decision && (scenario === "changed" || scenario === "matching");
  const applied =
    scenario === "applied" ||
    decision === "keep" ||
    decision === "2:3" ||
    decision === "3:3";
  const status = needsReview
    ? "Needs review"
    : applied
      ? "Applied"
      : scenario === "dropped"
        ? "Dropped"
        : "Discarded";
  const markerCell =
    scenario === "applied" || scenario === "changed"
      ? decision !== "discard"
        ? "2:3"
        : null
      : decision === "2:3" || decision === "3:3"
        ? decision
        : null;
  const resolve = (value: RerunDecision) => {
    setDecisions((current) => ({ ...current, [scenario]: value }));
    setConfirmDiscard(false);
    setPickerOpen(false);
  };
  const result =
    decision === "keep"
      ? "Edit kept. Alex’s bonus stays $250 in C2, and D2 stays $1,450. The edit no longer needs review."
      : decision === "discard"
        ? scenario === "matching"
          ? "Edit discarded. C2 stays $300 and C3 stays $150. Neither row receives the saved $250 edit, and it will not apply to future runs."
          : "Edit discarded. C2 now shows the new run’s $300 bonus, and D2 is $1,500. The saved $250 edit will not apply to future runs."
        : decision === "2:3"
          ? "Edit applied to C2. Alex’s $1,200 base pay plus the $250 bonus gives $1,450 in D2. The edit no longer needs review."
          : decision === "3:3"
            ? "The $250 bonus is now in C3. This is the other row: its base pay is $1,350. Select Reset and choose C2 to apply the sample correction to the intended row."
            : needsReview
              ? "Open Manual edits, select To review, and compare the saved edit with the new run. Try an action below, then check the grid."
              : scenario === "applied"
                ? "No action is needed for this edit. It appears under Applied, with a blue corner marker in C2."
                : "The edit is not in the grid. Check the missing employee before using the new workbook.";
  return (
    <GuidePreview
      id="payroll-rerun-example"
      label="Review edits after a rerun"
      caption="Choose a sample case. Compare the saved $250 bonus with the new run, then see what each action does."
      onReset={() => {
        setDecisions({});
        setPickerOpen(false);
        setConfirmDiscard(false);
        setVersion((value) => value + 1);
      }}
    >
      <Tabs
        value={scenario}
        onValueChange={(value) => {
          if (!(value in RERUN_SCENARIOS)) return;
          setScenario(value as RerunScenario);
          setConfirmDiscard(false);
          setPickerOpen(false);
        }}
      >
        <TabsList
          className="w-full justify-start"
          aria-label="Rerun sample cases"
        >
          {Object.entries(RERUN_SCENARIOS).map(([id, item]) => (
            <TabsTrigger key={id} value={id} className="px-3">
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={scenario} className="mt-0">
          <p className="px-4 py-3 text-sm text-subtle">
            {RERUN_SCENARIOS[scenario].description}
          </p>
          <PracticeGrid
            compact
            key={`${version}:${scenario}`}
            sheet={sheet}
            editedCellIds={markerCell && !needsReview ? [markerCell] : []}
            reviewCellIds={markerCell && needsReview ? [markerCell] : []}
            showSelectionNote={false}
          />
          <Stack
            gap={2}
            align="stretch"
            className="border-t border-line p-4 text-sm"
          >
            <Stack
              className="w-full"
              gap={4}
              direction="row"
              align="center"
              justify="between"
            >
              <p className="font-semibold">
                {decision === "discard" ? "Review result" : "Manual edits"}
                {decision === "discard"
                  ? ""
                  : needsReview
                    ? " → To review"
                    : applied
                      ? " → Applied"
                      : " → All"}
              </p>
              <Badge
                variant="secondary"
                color={
                  needsReview ? "warning" : applied ? "success" : "neutral"
                }
              >
                {status}
              </Badge>
            </Stack>
            <p>
              Alex Morgan · Bonus
              {markerCell ? ` · ${markerCell === "3:3" ? "C3" : "C2"}` : ""}
            </p>
            {decision === "discard" ? (
              <p>No saved edit remains for Alex’s bonus.</p>
            ) : (
              <EditValueChange from={200} to={250} />
            )}
            <RerunReviewActions
              scenario={scenario}
              decision={decision}
              pickerOpen={pickerOpen}
              confirmDiscard={confirmDiscard}
              onPickRow={() => setPickerOpen(true)}
              onDiscard={() => setConfirmDiscard(true)}
              onCancel={() => setConfirmDiscard(false)}
              onResolve={resolve}
            />
          </Stack>
          <ExampleNote>{result}</ExampleNote>
        </TabsContent>
      </Tabs>
    </GuidePreview>
  );
};
